import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import api from "../services/api";
import { downloadEsmpFile } from "../utils/fileDownload";

export default function SubmittedESMP() {
  const [search, setSearch] = useState("");
  const [submittedEsmp, setSubmittedEsmp] = useState([]);
  const [selectedEsmp, setSelectedEsmp] = useState(null);
  const [reviewer, setReviewer] = useState("");
  const [deadline, setDeadline] = useState("");
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewers, setReviewers] = useState([]);
  const [reviewersLoading, setReviewersLoading] = useState(true);
  const [reviewerError, setReviewerError] = useState("");

  const token = localStorage.getItem("token");

  // Fetch submitted ESMPs from backend
  useEffect(() => {
    fetchSubmittedEsmps();
    fetchReviewers();
  }, []);

  const fetchSubmittedEsmps = async () => {
    try {
      setLoading(true);
      const res = await api.get("/esmp/list", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = res.data?.data;
      const esmps = Array.isArray(payload) ? payload : (Array.isArray(payload?.esmps) ? payload.esmps : []);
      const sorted = [...esmps].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setSubmittedEsmp(sorted);
    } catch (err) {
      console.error("Error fetching submitted ESMPs:", err);
      alert("Failed to fetch submitted ESMPs");
    } finally {
      setLoading(false);
    }
  };

  // Fetch list of reviewers from backend
  const fetchReviewers = async () => {
    try {
      setReviewersLoading(true);
      setReviewerError("");
      const res = await api.get("/esmp/reviewers", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewers(res.data.data || []);
    } catch (err) {
      console.error("Error fetching reviewers:", err);
      setReviewers([]);
      setReviewerError(err.response?.data?.message || "Unable to load reviewers");
    } finally {
      setReviewersLoading(false);
    }
  };

  const filtered = submittedEsmp.filter(
    e =>
      (e.esmp_id || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.district || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.project_name || "").toLowerCase().includes(search.toLowerCase())
  );

  // Handle assigning reviewer
  const handleSend = async () => {
    if (!reviewer || !deadline) {
      return alert("Please select reviewer and deadline.");
    }

    try {
      setSending(true);
      
      await api.put(
        `/esmp/${selectedEsmp.id}/assign`,
        {
          reviewer_id: reviewer,
          deadline: deadline
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert(`Reviewer ${selectedEsmp.reviewer_id ? "reassigned" : "assigned"} successfully for ESMP ${selectedEsmp.esmp_id}`);
      
      // Refresh the list
      await fetchSubmittedEsmps();
      
      // Close modal
      setSelectedEsmp(null);
      setReviewer("");
      setDeadline("");
    } catch (err) {
      console.error("Error assigning reviewer:", err);
      alert(err.response?.data?.message || "Failed to assign reviewer");
    } finally {
      setSending(false);
    }
  };

  const openReviewerModal = (esmp) => {
    setSelectedEsmp(esmp);
    setReviewer("");
    setDeadline("");
  };

  const handleDelete = async (esmp) => {
    const confirmed = window.confirm(
      `Delete ESMP ${esmp.esmp_id} and its uploaded document? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setDeletingId(esmp.id);
      await api.delete(`/esmp/${esmp.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmittedEsmp((current) => current.filter((item) => item.id !== esmp.id));
      alert(`ESMP ${esmp.esmp_id} deleted successfully`);
    } catch (err) {
      console.error("Error deleting ESMP:", err);
      alert(err.response?.data?.message || "Failed to delete ESMP");
    } finally {
      setDeletingId(null);
    }
  };

  const handleActionChange = (event, esmp) => {
    const action = event.target.value;
    event.target.value = "";

    if (action === "assign" || action === "reassign") {
      openReviewerModal(esmp);
    } else if (action === "delete") {
      handleDelete(esmp);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <main className="flex-1 p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">Submitted ESMPs</h2>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Search ESMP..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="px-3 py-2 rounded text-black border focus:ring-2 focus:ring-green-600 outline-none"
            />
            <button
              onClick={fetchSubmittedEsmps}
              className="px-4 py-2 bg-white text-green-700 rounded-lg font-semibold hover:bg-gray-100"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white p-4 rounded-xl shadow">
          <p className="text-sm text-gray-600">
            Total ESMPs: <span className="font-bold text-green-700">{submittedEsmp.length}</span>
          </p>
        </div>

        {/* Table */}
        <div className="bg-white p-6 rounded-xl shadow">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading submitted ESMPs...</p>
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-6">No ESMPs found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-3 py-2 text-left">ESMP ID</th>
                    <th className="border px-3 py-2 text-left">District</th>
                    <th className="border px-3 py-2 text-left">Project Name</th>
                    <th className="border px-3 py-2 text-left">Submitted By</th>
                    <th className="border px-3 py-2 text-left">Submitted Date</th>
                    <th className="border px-3 py-2 text-left">Document</th>
                    <th className="border px-3 py-2 text-left">Status</th>
                    <th className="border px-3 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item) => {
                    const isReviewed = ["Approved", "Returned", "Rejected"].includes(item.status);
                    const isAssigned = !!item.reviewer_id;
                    return (
                    <tr key={item.id} className="hover:bg-green-50">
                      <td className="border px-2 py-2 text-blue-600 font-medium">
                        {item.esmp_id}
                      </td>
                      <td className="border px-2 py-2">{item.district}</td>
                      <td className="border px-2 py-2">{item.project_name}</td>
                      <td className="border px-2 py-2">
                        {item.User ? item.User.name : 'N/A'}
                      </td>
                      <td className="border px-2 py-2 text-xs text-gray-500">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </td>
                      <td className="border px-2 py-2">
                        <button
                          type="button"
                          onClick={() => downloadEsmpFile(item)}
                          className="text-blue-600 hover:underline"
                        >
                          {item.file_name || "N/A"}
                        </button>
                      </td>
                      <td className="border px-2 py-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                          {item.status}
                        </span>
                      </td>
                      <td className="border px-2 py-2 text-center">
                        <select
                          defaultValue=""
                          onChange={(event) => handleActionChange(event, item)}
                          disabled={deletingId === item.id}
                          className={`rounded border px-3 py-1.5 text-xs font-semibold outline-none focus:ring-2 focus:ring-green-600 ${
                            deletingId === item.id
                              ? "cursor-not-allowed bg-gray-200 text-gray-500"
                              : "cursor-pointer bg-green-700 text-white"
                          }`}
                          aria-label={`Actions for ESMP ${item.esmp_id}`}
                        >
                          <option value="" disabled>
                            {deletingId === item.id ? "Deleting..." : "Actions"}
                          </option>
                          {!isAssigned && !isReviewed && (
                            <option value="assign">Assign Reviewer</option>
                          )}
                          {isAssigned && !isReviewed && (
                            <option value="reassign">Reassign Reviewer</option>
                          )}
                          <option value="delete">Delete ESMP</option>
                        </select>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal for assigning reviewer */}
      {selectedEsmp && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white w-full max-w-md p-6 rounded-xl shadow-lg space-y-4">
            <h3 className="text-xl font-bold text-green-700">
              {selectedEsmp.reviewer_id ? "Reassign Reviewer" : "Assign Reviewer"}
            </h3>
            <div className="space-y-2 text-sm">
              <p><span className="font-semibold">ESMP ID:</span> {selectedEsmp.esmp_id}</p>
              <p><span className="font-semibold">Project:</span> {selectedEsmp.project_name}</p>
              <p><span className="font-semibold">District:</span> {selectedEsmp.district}</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Select Reviewer</label>
                <select
                  value={reviewer}
                  onChange={e => setReviewer(e.target.value)}
                  disabled={reviewersLoading || reviewers.length === 0}
                  className="border w-full p-2 rounded"
                >
                  <option value="">
                    {reviewersLoading
                      ? "Loading reviewers..."
                      : reviewers.length === 0
                      ? "-- No reviewer accounts available --"
                      : "-- Select Reviewer --"}
                  </option>
                  {reviewers.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
                {reviewerError && (
                  <p className="mt-1 text-xs text-red-600">{reviewerError}</p>
                )}
                {!reviewersLoading && !reviewerError && reviewers.length === 0 && (
                  <p className="mt-1 text-xs text-amber-700">
                    Create a user with the Reviewer role before assigning an ESMP.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Review Deadline</label>
                <input
                  type="datetime-local"
                  value={deadline}
                  onChange={e => setDeadline(e.target.value)}
                  className="border w-full p-2 rounded"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3">
              <button
                onClick={() => {
                  setSelectedEsmp(null);
                  setReviewer("");
                  setDeadline("");
                }}
                className="px-4 py-2 rounded border hover:bg-gray-200"
              >
                Cancel
              </button>

              <button
                onClick={handleSend}
                disabled={sending}
                className={`px-4 py-2 rounded text-white ${
                  sending
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-700 hover:bg-green-800"
                }`}
              >
                {sending
                  ? selectedEsmp.reviewer_id ? "Reassigning..." : "Assigning..."
                  : selectedEsmp.reviewer_id ? "Reassign Reviewer" : "Assign Reviewer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
